"use client";
import {
  AlertCircle,
  AlertTriangle,
  ChevronRight,
  Clock,
  ExternalLink,
  Gauge,
  HelpCircle,
  Key,
  Lock,
  RefreshCw,
  Server,
  ShieldAlert,
  UserX,
  ZapOff,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type ErrorType = keyof typeof ErrorTypes;

type ErrorMessageProps = {
  type?: ErrorType;
  title?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  onRetry?: () => void;
  className?: string;
  showRetry?: boolean;
  showHelp?: boolean;
  helpUrl?: string;
  isRetrying?: boolean;
};

export function ErrorMessage({
  type = "GENERIC_ERROR",
  title,
  message,
  actionLabel,
  onAction,
  onRetry,
  className = "",
  showRetry = true,
  showHelp = true,
  helpUrl,
  isRetrying = false,
}: ErrorMessageProps) {
  const [isHovered, setIsHovered] = useState(false);

  const errorDefaults = {
    GENERIC_ERROR: {
      icon: AlertCircle,
      title: "Something Went Wrong",
      message: "An unexpected error occurred. Please try again later.",
      gradient: "from-slate-50 to-slate-100 dark:from-slate-900/50 dark:to-slate-800/50",
      iconBg: "bg-slate-100 dark:bg-slate-800",
      iconColor: "text-slate-600 dark:text-slate-400",
      borderColor: "border-slate-200 dark:border-slate-700",
    },
    AUTH_ERROR: {
      icon: Lock,
      title: "Authentication Required",
      message: "Please sign in to continue accessing this resource.",
      gradient: "from-purple-50 to-purple-100 dark:from-purple-950/50 dark:to-purple-900/50",
      iconBg: "bg-purple-100 dark:bg-purple-900/50",
      iconColor: "text-purple-600 dark:text-purple-400",
      borderColor: "border-purple-200 dark:border-purple-800",
    },
    FORBIDDEN: {
      icon: ShieldAlert,
      title: "Access Denied",
      message: "You don't have permission to access this resource.",
      gradient: "from-red-50 to-red-100 dark:from-red-950/50 dark:to-red-900/50",
      iconBg: "bg-red-100 dark:bg-red-900/50",
      iconColor: "text-red-600 dark:text-red-400",
      borderColor: "border-red-200 dark:border-red-800",
    },
    VALIDATION_ERROR: {
      icon: AlertTriangle,
      title: "Invalid Input",
      message: "Please check your information and try again.",
      gradient: "from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/50",
      iconBg: "bg-amber-100 dark:bg-amber-900/50",
      iconColor: "text-amber-600 dark:text-amber-400",
      borderColor: "border-amber-200 dark:border-amber-800",
    },
    NOT_FOUND: {
      icon: AlertCircle,
      title: "Not Found",
      message: "The requested resource could not be found.",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50",
      iconBg: "bg-blue-100 dark:bg-blue-900/50",
      iconColor: "text-blue-600 dark:text-blue-400",
      borderColor: "border-blue-200 dark:border-blue-800",
    },
    RATE_LIMIT_EXCEEDED: {
      icon: Gauge,
      title: "Too Many Requests",
      message: "Please wait a moment before trying again.",
      gradient: "from-orange-50 to-orange-100 dark:from-orange-950/50 dark:to-orange-900/50",
      iconBg: "bg-orange-100 dark:bg-orange-900/50",
      iconColor: "text-orange-600 dark:text-orange-400",
      borderColor: "border-orange-200 dark:border-orange-800",
    },
    CONFLICT: {
      icon: AlertTriangle,
      title: "Conflict Detected",
      message: "There was a conflict with the current state of the resource.",
      gradient: "from-yellow-50 to-yellow-100 dark:from-yellow-950/50 dark:to-yellow-900/50",
      iconBg: "bg-yellow-100 dark:bg-yellow-900/50",
      iconColor: "text-yellow-600 dark:text-yellow-400",
      borderColor: "border-yellow-200 dark:border-yellow-800",
    },
    BAD_REQUEST: {
      icon: AlertTriangle,
      title: "Invalid Request",
      message: "The server couldn't understand your request.",
      gradient: "from-rose-50 to-rose-100 dark:from-rose-950/50 dark:to-rose-900/50",
      iconBg: "bg-rose-100 dark:bg-rose-900/50",
      iconColor: "text-rose-600 dark:text-rose-400",
      borderColor: "border-rose-200 dark:border-rose-800",
    },
    SERVICE_UNAVAILABLE: {
      icon: Server,
      title: "Service Unavailable",
      message: "Our service is temporarily down. We're working to fix it.",
      gradient: "from-indigo-50 to-indigo-100 dark:from-indigo-950/50 dark:to-indigo-900/50",
      iconBg: "bg-indigo-100 dark:bg-indigo-900/50",
      iconColor: "text-indigo-600 dark:text-indigo-400",
      borderColor: "border-indigo-200 dark:border-indigo-800",
    },
    TIMEOUT: {
      icon: Clock,
      title: "Request Timeout",
      message: "The request took too long. Please check your connection.",
      gradient: "from-cyan-50 to-cyan-100 dark:from-cyan-950/50 dark:to-cyan-900/50",
      iconBg: "bg-cyan-100 dark:bg-cyan-900/50",
      iconColor: "text-cyan-600 dark:text-cyan-400",
      borderColor: "border-cyan-200 dark:border-cyan-800",
    },
    UNAUTHORIZED: {
      icon: Key,
      title: "Authentication Required",
      message: "Please log in to access this resource.",
      gradient: "from-violet-50 to-violet-100 dark:from-violet-950/50 dark:to-violet-900/50",
      iconBg: "bg-violet-100 dark:bg-violet-900/50",
      iconColor: "text-violet-600 dark:text-violet-400",
      borderColor: "border-violet-200 dark:border-violet-800",
    },
    SERVER_ERROR: {
      icon: Server,
      title: "Server Error",
      message: "Our servers are experiencing issues. Please try again later.",
      gradient: "from-red-50 to-red-100 dark:from-red-950/50 dark:to-red-900/50",
      iconBg: "bg-red-100 dark:bg-red-900/50",
      iconColor: "text-red-600 dark:text-red-400",
      borderColor: "border-red-200 dark:border-red-800",
    },
    USER_NOT_FOUND: {
      icon: UserX,
      title: "User Not Found",
      message: "The requested user account doesn't exist.",
      gradient: "from-sky-50 to-sky-100 dark:from-sky-950/50 dark:to-sky-900/50",
      iconBg: "bg-sky-100 dark:bg-sky-900/50",
      iconColor: "text-sky-600 dark:text-sky-400",
      borderColor: "border-sky-200 dark:border-sky-800",
    },
    INVALID_CREDENTIALS: {
      icon: ZapOff,
      title: "Invalid Credentials",
      message: "The email or password you entered is incorrect.",
      gradient: "from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/50",
      iconBg: "bg-amber-100 dark:bg-amber-900/50",
      iconColor: "text-amber-600 dark:text-amber-400",
      borderColor: "border-amber-200 dark:border-amber-800",
    },
  };

  const errorInfo = errorDefaults[type] || errorDefaults.GENERIC_ERROR;
  const ErrorIcon = errorInfo.icon;

  const handleHelpClick = () => {
    if (helpUrl) {
      window.open(helpUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Card
      className={cn(
        "relative overflow-hidden border transition-all duration-300 ease-out",
        "hover:shadow-lg hover:shadow-black/5 dark:hover:shadow-black/20",
        "hover:-translate-y-0.5",
        errorInfo.borderColor,
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-br opacity-60 transition-opacity duration-300",
          errorInfo.gradient,
          isHovered ? "opacity-80" : "opacity-60",
        )}
      />

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.15)_1px,transparent_0)] [background-size:20px_20px] dark:bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.05)_1px,transparent_0)]" />

      <CardContent className="relative p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
          <div className="flex justify-center sm:justify-start">
            <div
              className={cn(
                "relative flex h-12 w-12 items-center justify-center rounded-xl shadow-sm transition-all duration-300",
                "ring-1 ring-black/5 dark:ring-white/10",
                errorInfo.iconBg,
                isHovered && "scale-105 shadow-md",
              )}
            >
              <ErrorIcon className={cn("h-6 w-6 transition-colors duration-200", errorInfo.iconColor)} />

              <div
                className={cn(
                  "absolute inset-0 rounded-xl opacity-0 transition-opacity duration-300",
                  "bg-gradient-to-br from-white/20 to-transparent",
                  isHovered && "opacity-100",
                )}
              />
            </div>
          </div>

          <div className="flex-1 space-y-3 text-center sm:text-left">
            <div className="space-y-1">
              <h3 className="text-lg font-semibold tracking-tight text-foreground">{title || errorInfo.title}</h3>
              <p className="text-sm leading-relaxed text-muted-foreground">{message || errorInfo.message}</p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 pt-1 sm:justify-start">
              {showRetry && onRetry && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                  disabled={isRetrying}
                  className="group relative overflow-hidden transition-all duration-200 hover:shadow-sm bg-transparent"
                >
                  <RefreshCw
                    className={cn("mr-2 h-4 w-4 transition-transform duration-200", isRetrying && "animate-spin")}
                  />
                  {isRetrying ? "Retrying..." : "Try Again"}

                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                </Button>
              )}

              {actionLabel && onAction && (
                <Button
                  size="sm"
                  onClick={onAction}
                  className="group relative overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md"
                >
                  {actionLabel}
                  <ChevronRight className="ml-1 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />

                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500" />
                </Button>
              )}

              {showHelp && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleHelpClick}
                  className="group text-muted-foreground transition-all duration-200 hover:text-foreground"
                >
                  <HelpCircle className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:scale-110" />
                  Get Help
                  {helpUrl && <ExternalLink className="ml-1 h-3 w-3 opacity-60" />}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export enum ErrorTypes {
  GENERIC_ERROR = "GENERIC_ERROR",
  AUTH_ERROR = "AUTH_ERROR",
  FORBIDDEN = "FORBIDDEN",
  VALIDATION_ERROR = "VALIDATION_ERROR",
  NOT_FOUND = "NOT_FOUND",
  CONFLICT = "CONFLICT",
  BAD_REQUEST = "BAD_REQUEST",
  SERVICE_UNAVAILABLE = "SERVICE_UNAVAILABLE",
  TIMEOUT = "TIMEOUT",
  UNAUTHORIZED = "UNAUTHORIZED",
  SERVER_ERROR = "SERVER_ERROR",
  RATE_LIMIT_EXCEEDED = "RATE_LIMIT_EXCEEDED",
  USER_NOT_FOUND = "USER_NOT_FOUND",
  INVALID_CREDENTIALS = "INVALID_CREDENTIALS",
}
